import { Box, Grid, Typography } from "@mui/material";
import {
  ChartsGrid,
  ChartsLegend,
  ChartsXAxis,
  ChartsYAxis,
  LineChartPro,
  lineElementClasses,
  LinePlot,
  markElementClasses,
  MarkPlot,
} from "@mui/x-charts-pro";
import { format } from "date-fns";
import { CustomItemTooltip } from "../../../../../ResuableChartTooltip/CustomItemTooltip";
import { useSelector } from "react-redux";

const PerformanceDashboardRealUsersExperience = () => {
  const { exequteQueryContentQuery5, exequteQueryContentQuery6, selectedPage } =
    useSelector((state: any) => state.pageSpeedPerformance);
  const GraphLCP =
    exequteQueryContentQuery5?.payload?.map((item: any) => ({
      x: format(new Date(item?.DATE), "dd-MM-yyyy") || "No Date",
      y: parseFloat((item?.Avg_Real_Users_Experience_LCP || 0).toFixed(2)),
    })) || [];

  const GraphPageSpecificLCP =
    exequteQueryContentQuery6?.payload
      ?.filter((item: any) => item.page_name === selectedPage?.page_name)
      ?.map((item: any) => ({
        x: format(new Date(item?.DATE), "dd-MM-yyyy"), // Format the date
        y: parseFloat((item?.Real_Users_Experience_LCP || 0).toFixed(2)),
      })) || []; // Fallback to an empty array if data is unavailable

  const GraphLCPTarget =
    exequteQueryContentQuery5?.payload?.map((item: any) => ({
      x: format(new Date(item?.DATE), "dd-MM-yyyy") || "No Date",
      z: parseFloat((item?.lcp_target || 0).toFixed(2)),
    })) || [];

  const GraphPageSpecificLCPTarget =
    exequteQueryContentQuery6?.payload
      ?.filter((item: any) => item.page_name === selectedPage?.page_name)
      ?.map((item: any) => ({
        x: format(new Date(item?.DATE), "dd-MM-yyyy"), // Format the date
        z: parseFloat((item?.lcp_target || 0).toFixed(2)),
      })) || []; // Fallback to an empty array if data is unavailable

  const GraphLCPAchievement =
    exequteQueryContentQuery5?.payload?.map((item: any) => ({
      x: format(new Date(item?.DATE), "dd-MM-yyyy") || "No Date",
      s: parseFloat((item?.achievement_Real_Users_lcp || 0).toFixed(2)),
    })) || [];

  const GraphLCPAchievementPageSpecific =
    exequteQueryContentQuery6?.payload
      ?.filter((item: any) => item.page_name === selectedPage?.page_name)
      ?.map((item: any) => ({
        x: format(new Date(item?.DATE), "dd-MM-yyyy"), // Format the date
        s: parseFloat((item?.achievement_Real_Users_lcp || 0).toFixed(2)),
      })) || []; // Fallback to an empty array if data is unavailable

  const uniqueDatesLCP = Array.from(
    new Set([
      ...GraphLCP.map((item: any) => item.x),
      ...GraphLCPTarget.map((item: any) => item.x),
      ...GraphLCPAchievement.map((item: any) => item.x),
    ])
  );

  const uniqueDatesPageSpecificLCP = Array.from(
    new Set([
      ...GraphPageSpecificLCP.map((item: any) => item.x),
      ...GraphPageSpecificLCPTarget.map((item: any) => item.x),
      ...GraphLCPAchievement.map((item: any) => item.x),
      ...GraphLCPAchievementPageSpecific.map((item: any) => item.x),
    ])
  );

  const combinedDatasetLCP = selectedPage
    ? uniqueDatesPageSpecificLCP.map((date) => {
        const data = GraphPageSpecificLCP.find((item: any) => item.x === date);
        const target = GraphPageSpecificLCPTarget.find(
          (item: any) => item.x === date
        );
        const achievement = GraphLCPAchievementPageSpecific.find(
          (item: any) => item.x === date
        );
        const [year, month, day] = date.split("-"); // ** Date conversion logic (yyyy-mm-dd -> dd-mm-yyyy) **
        const formattedDate =
          day && month && year ? `${day}-${month}-${year}` : date; // If date is valid, convert it
        return {
          x: formattedDate,
          y: data?.y || 0,
          z: target?.z || 0,
          s: achievement?.s || 0,
        };
      })
    : uniqueDatesLCP.map((date) => {
        const data = GraphLCP.find((item: any) => item.x === date);
        const target = GraphLCPTarget.find((item: any) => item.x === date);
        const achievement = GraphLCPAchievement.find(
          (item: any) => item.x === date
        );
        const [year, month, day] = date.split("-"); // ** Date conversion logic (yyyy-mm-dd -> dd-mm-yyyy) **
        const formattedDate =
          day && month && year ? `${day}-${month}-${year}` : date; // If date is valid, convert it
        return {
          x: formattedDate,
          y: data?.y || 0,
          z: target?.z || 0,
          s: achievement?.s || 0,
        };
      });

  const GraphINP =
    exequteQueryContentQuery5?.payload?.map((item: any) => ({
      x: format(new Date(item?.DATE), "dd-MM-yyyy") || "No Date",
      y: parseFloat((item?.Avg_Real_Users_Experience_INP || 0).toFixed(2)),
    })) || [];

  const GraphPageSpecificINP =
    exequteQueryContentQuery6?.payload
      ?.filter((item: any) => item.page_name === selectedPage?.page_name)
      ?.map((item: any) => ({
        x: format(new Date(item?.DATE), "dd-MM-yyyy"), // Format the date
        y: parseFloat((item?.Real_Users_Experience_INP || 0).toFixed(2)),
      })) || []; // Fallback to an empty array if data is unavailable

  const GraphINPTarget =
    exequteQueryContentQuery5?.payload?.map((item: any) => ({
      x: format(new Date(item?.DATE), "dd-MM-yyyy") || "No Date",
      z: parseFloat((item?.inp_target || 0).toFixed(2)),
    })) || [];

  const GraphINPAchievement =
    exequteQueryContentQuery5?.payload?.map((item: any) => ({
      x: format(new Date(item?.DATE), "dd-MM-yyyy") || "No Date",
      s: parseFloat(((item?.achievement_Real_Users_inp || 0) * 100).toFixed(2)),
    })) || [];

  const GraphINPAchievementPageSpecific =
    exequteQueryContentQuery6?.payload
      ?.filter((item: any) => item.page_name === selectedPage?.page_name)
      ?.map((item: any) => ({
        x: format(new Date(item?.DATE), "dd-MM-yyyy"), // Format the date
        s: parseFloat(
          ((item?.achievement_Real_Users_inp || 0) * 100).toFixed(2)
        ),
      })) || []; // Fallback to an empty array if data is unavailable

  const GraphPageSpecificINPTarget =
    exequteQueryContentQuery6?.payload
      ?.filter((item: any) => item.page_name === selectedPage?.page_name)
      ?.map((item: any) => ({
        x: format(new Date(item?.DATE), "dd-MM-yyyy"), // Format the date
        z: parseFloat((item?.inp_target || 0).toFixed(2)),
      })) || []; // Fallback to an empty array if data is unavailable

  const uniqueDatesINP = Array.from(
    new Set([
      ...GraphINP.map((item: any) => item.x),
      ...GraphINPTarget.map((item: any) => item.x),
      ...GraphINPAchievement.map((item: any) => item.x),
    ])
  );

  const uniqueDatesPageSpecificINP = Array.from(
    new Set([
      ...GraphPageSpecificINP.map((item: any) => item.x),
      ...GraphPageSpecificINPTarget.map((item: any) => item.x),
      ...GraphINPAchievementPageSpecific.map((item: any) => item.x),
    ])
  );

  const combinedDatasetINP = selectedPage
    ? uniqueDatesPageSpecificINP.map((date) => {
        const data = GraphPageSpecificINP.find((item: any) => item.x === date);
        const target = GraphPageSpecificINPTarget.find(
          (item: any) => item.x === date
        );
        const achievement = GraphINPAchievementPageSpecific.find(
          (item: any) => item.x === date
        );
        const [year, month, day] = date.split("-"); // ** Date conversion logic (yyyy-mm-dd -> dd-mm-yyyy) **
        const formattedDate =
          day && month && year ? `${day}-${month}-${year}` : date; // If date is valid, convert it
        return {
          x: formattedDate,
          y: data?.y || 0,
          z: target?.z || 0,
          s: achievement?.s || 0,
        };
      })
    : uniqueDatesINP.map((date) => {
        const data = GraphINP.find((item: any) => item.x === date);
        const target = GraphINPTarget.find((item: any) => item.x === date);
        const achievement = GraphINPAchievement.find(
          (item: any) => item.x === date
        );
        const [year, month, day] = date.split("-"); // ** Date conversion logic (yyyy-mm-dd -> dd-mm-yyyy) **
        const formattedDate =
          day && month && year ? `${day}-${month}-${year}` : date; // If date is valid, convert it
        return {
          x: formattedDate,
          y: data?.y || 0,
          z: target?.z || 0,
          s: achievement?.s || 0,
        };
      });

  const GraphCLS =
    exequteQueryContentQuery5?.payload?.map((item: any) => ({
      x: format(new Date(item?.DATE), "dd-MM-yyyy") || "No Date",
      y: parseFloat((item?.Avg_Real_Users_Experience_CLS || 0).toFixed(2)),
    })) || [];

  const GraphPageSpecificCLS =
    exequteQueryContentQuery6?.payload
      ?.filter((item: any) => item.page_name === selectedPage?.page_name)
      ?.map((item: any) => ({
        x: format(new Date(item?.DATE), "dd-MM-yyyy"), // Format the date
        y: parseFloat((item?.Real_Users_Experience_CLS || 0).toFixed(2)),
      })) || []; // Fallback to an empty array if data is unavailable

  const GraphCLSTarget =
    exequteQueryContentQuery5?.payload?.map((item: any) => ({
      x: format(new Date(item?.DATE), "dd-MM-yyyy") || "No Date",
      z: parseFloat((item?.cls_target || 0).toFixed(2)),
    })) || [];

  const GraphPageSpecificCLSTarget =
    exequteQueryContentQuery6?.payload
      ?.filter((item: any) => item.page_name === selectedPage?.page_name)
      ?.map((item: any) => ({
        x: format(new Date(item?.DATE), "dd-MM-yyyy"), // Format the date
        z: parseFloat((item?.cls_target || 0).toFixed(2)),
      })) || []; // Fallback to an empty array if data is unavailable

  const GraphCLSAchievement =
    exequteQueryContentQuery5?.payload?.map((item: any) => ({
      x: format(new Date(item?.DATE), "dd-MM-yyyy") || "No Date",
      s: parseFloat((item?.achievement_Real_Users_cls || 0).toFixed(2)),
    })) || [];

  const GraphCLSAchievementPageSpecific =
    exequteQueryContentQuery6?.payload
      ?.filter((item: any) => item.page_name === selectedPage?.page_name)
      ?.map((item: any) => ({
        x: format(new Date(item?.DATE), "dd-MM-yyyy"), // Format the date
        s: parseFloat((item?.achievement_Real_Users_cls || 0).toFixed(2)),
      })) || []; // Fallback to an empty array if data is unavailable

  const uniqueDatesCLS = Array.from(
    new Set([
      ...GraphCLS.map((item: any) => item.x),
      ...GraphCLSTarget.map((item: any) => item.x),
      ...GraphCLSAchievement.map((item: any) => item.x),
    ])
  );

  const uniqueDatesPageSpecificCLS = Array.from(
    new Set([
      ...GraphPageSpecificCLS.map((item: any) => item.x),
      ...GraphPageSpecificCLSTarget.map((item: any) => item.x),
      ...GraphCLSAchievementPageSpecific.map((item: any) => item.x),
    ])
  );

  const combinedDatasetCLS = selectedPage
    ? uniqueDatesPageSpecificCLS.map((date) => {
        const data = GraphPageSpecificCLS.find((item: any) => item.x === date);
        const target = GraphPageSpecificCLSTarget.find(
          (item: any) => item.x === date
        );
        const achievement = GraphCLSAchievementPageSpecific.find(
          (item: any) => item.x === date
        );
        const [year, month, day] = date.split("-"); // ** Date conversion logic (yyyy-mm-dd -> dd-mm-yyyy) **
        const formattedDate =
          day && month && year ? `${day}-${month}-${year}` : date; // If date is valid, convert it
        return {
          x: formattedDate,
          y: data?.y || 0,
          z: target?.z || 0,
          s: achievement?.s || 0,
        };
      })
    : uniqueDatesCLS.map((date) => {
        const data = GraphCLS.find((item: any) => item.x === date);
        const target = GraphCLSTarget.find((item: any) => item.x === date);
        const achievement = GraphCLSAchievement.find(
          (item: any) => item.x === date
        );
        const [year, month, day] = date.split("-"); // ** Date conversion logic (yyyy-mm-dd -> dd-mm-yyyy) **
        const formattedDate =
          day && month && year ? `${day}-${month}-${year}` : date; // If date is valid, convert it
        return {
          x: formattedDate,
          y: data?.y || 0,
          z: target?.z || 0,
          s: achievement?.s || 0,
        };
      });

  const GraphFCP =
    exequteQueryContentQuery5?.payload?.map((item: any) => ({
      x: format(new Date(item?.DATE), "dd-MM-yyyy") || "No Date",
      y: parseFloat((item?.Avg_Real_Users_Experience_FCP || 0).toFixed(2)),
    })) || [];

  const GraphPageSpecificFCP =
    exequteQueryContentQuery6?.payload
      ?.filter((item: any) => item.page_name === selectedPage?.page_name)
      ?.map((item: any) => ({
        x: format(new Date(item?.DATE), "dd-MM-yyyy"), // Format the date
        y: parseFloat((item?.Real_Users_Experience_FCP || 0).toFixed(2)),
      })) || []; // Fallback to an empty array if data is unavailable

  const GraphFCPTarget =
    exequteQueryContentQuery5?.payload?.map((item: any) => ({
      x: format(new Date(item?.DATE), "dd-MM-yyyy") || "No Date",
      z: parseFloat((item?.fcp_target || 0).toFixed(2)),
    })) || [];

  const GraphPageSpecificFCPTarget =
    exequteQueryContentQuery6?.payload
      ?.filter((item: any) => item.page_name === selectedPage?.page_name)
      ?.map((item: any) => ({
        x: format(new Date(item?.DATE), "dd-MM-yyyy"), // Format the date
        z: parseFloat((item?.fcp_target || 0).toFixed(2)),
      })) || []; // Fallback to an empty array if data is unavailable

  const GraphFCPAchievement =
    exequteQueryContentQuery5?.payload?.map((item: any) => ({
      x: format(new Date(item?.DATE), "dd-MM-yyyy") || "No Date",
      s: parseFloat((item?.achievement_Real_Users_fcp || 0).toFixed(2)),
    })) || [];

  const GraphFCPAchievementPageSpecific =
    exequteQueryContentQuery6?.payload
      ?.filter((item: any) => item.page_name === selectedPage?.page_name)
      ?.map((item: any) => ({
        x: format(new Date(item?.DATE), "dd-MM-yyyy"), // Format the date
        s: parseFloat((item?.achievement_Real_Users_fcp || 0).toFixed(2)),
      })) || []; // Fallback to an empty array if data is unavailable

  const uniqueDatesFCP = Array.from(
    new Set([
      ...GraphFCP.map((item: any) => item.x),
      ...GraphFCPTarget.map((item: any) => item.x),
      ...GraphFCPAchievement.map((item: any) => item.x),
    ])
  );

  const uniqueDatesPageSpecificFCP = Array.from(
    new Set([
      ...GraphPageSpecificFCP.map((item: any) => item.x),
      ...GraphPageSpecificFCPTarget.map((item: any) => item.x),
      ...GraphFCPAchievementPageSpecific.map((item: any) => item.x),
    ])
  );

  const combinedDatasetFCP = selectedPage
    ? uniqueDatesPageSpecificFCP.map((date) => {
        const data = GraphPageSpecificFCP.find((item: any) => item.x === date);
        const target = GraphPageSpecificFCPTarget.find(
          (item: any) => item.x === date
        );
        const achievement = GraphFCPAchievementPageSpecific.find(
          (item: any) => item.x === date
        );
        const [year, month, day] = date.split("-"); // ** Date conversion logic (yyyy-mm-dd -> dd-mm-yyyy) **
        const formattedDate =
          day && month && year ? `${day}-${month}-${year}` : date; // If date is valid, convert it
        return {
          x: formattedDate,
          y: data?.y || 0,
          z: target?.z || 0,
          s: achievement?.s || 0,
        };
      })
    : uniqueDatesFCP.map((date) => {
        const data = GraphFCP.find((item: any) => item.x === date);
        const target = GraphFCPTarget.find((item: any) => item.x === date);
        const achievement = GraphFCPAchievement.find(
          (item: any) => item.x === date
        );
        const [year, month, day] = date.split("-"); // ** Date conversion logic (yyyy-mm-dd -> dd-mm-yyyy) **
        const formattedDate =
          day && month && year ? `${day}-${month}-${year}` : date; // If date is valid, convert it
        return {
          x: formattedDate,
          y: data?.y || 0,
          z: target?.z || 0,
          s: achievement?.s || 0,
        };
      });

  const GraphTTFB =
    exequteQueryContentQuery5?.payload?.map((item: any) => ({
      x: format(new Date(item?.DATE), "dd-MM-yyyy") || "No Date",
      y: parseFloat((item?.Avg_Real_Users_Experience_TTFB || 0).toFixed(2)),
    })) || [];

  const GraphPageSpecificTTFB =
    exequteQueryContentQuery6?.payload
      ?.filter((item: any) => item.page_name === selectedPage?.page_name)
      ?.map((item: any) => ({
        x: format(new Date(item?.DATE), "dd-MM-yyyy"), // Format the date
        y: parseFloat((item?.Real_Users_Experience_TTFB || 0).toFixed(2)),
      })) || []; // Fallback to an empty array if data is unavailable

  const GraphTTFBTarget =
    exequteQueryContentQuery5?.payload?.map((item: any) => ({
      x: format(new Date(item?.DATE), "dd-MM-yyyy") || "No Date",
      z: parseFloat((item?.ttfb_target || 0).toFixed(2)),
    })) || [];

  const GraphPageSpecificTTFBTarget =
    exequteQueryContentQuery6?.payload
      ?.filter((item: any) => item.page_name === selectedPage?.page_name)
      ?.map((item: any) => ({
        x: format(new Date(item?.DATE), "dd-MM-yyyy"), // Format the date
        z: parseFloat((item?.ttfb_target || 0).toFixed(2)),
      })) || []; // Fallback to an empty array if data is unavailable

  const GraphTTFBAchievement =
    exequteQueryContentQuery5?.payload?.map((item: any) => ({
      x: format(new Date(item?.DATE), "dd-MM-yyyy") || "No Date",
      s: parseFloat((item?.achievement_Real_Users_ttfb || 0).toFixed(2)),
    })) || [];

  const GraphTTFBAchievementPageSpecific =
    exequteQueryContentQuery6?.payload
      ?.filter((item: any) => item.page_name === selectedPage?.page_name)
      ?.map((item: any) => ({
        x: format(new Date(item?.DATE), "dd-MM-yyyy"), // Format the date
        s: parseFloat((item?.achievement_Real_Users_ttfb || 0).toFixed(2)),
      })) || []; // Fallback to an empty array if data is unavailable

  const uniqueDatesTTFB = Array.from(
    new Set([
      ...GraphTTFB.map((item: any) => item.x),
      ...GraphTTFBTarget.map((item: any) => item.x),
      ...GraphTTFBAchievement.map((item: any) => item.x),
    ])
  );

  const uniqueDatesPageSpecificTTFB = Array.from(
    new Set([
      ...GraphPageSpecificTTFB.map((item: any) => item.x),
      ...GraphPageSpecificTTFBTarget.map((item: any) => item.x),
      ...GraphTTFBAchievementPageSpecific.map((item: any) => item.x),
    ])
  );

  const combinedDatasetTTFB = selectedPage
    ? uniqueDatesPageSpecificTTFB.map((date) => {
        const data = GraphPageSpecificTTFB.find((item: any) => item.x === date);
        const target = GraphPageSpecificTTFBTarget.find(
          (item: any) => item.x === date
        );
        const achievement = GraphTTFBAchievementPageSpecific.find(
          (item: any) => item.x === date
        );
        const [year, month, day] = date.split("-"); // ** Date conversion logic (yyyy-mm-dd -> dd-mm-yyyy) **
        const formattedDate =
          day && month && year ? `${day}-${month}-${year}` : date; // If date is valid, convert it
        return {
          x: formattedDate,
          y: data?.y || 0,
          z: target?.z || 0,
          s: achievement?.s || 0,
        };
      })
    : uniqueDatesTTFB.map((date) => {
        const data = GraphTTFB.find((item: any) => item.x === date);
        const target = GraphTTFBTarget.find((item: any) => item.x === date);
        const achievement = GraphTTFBAchievement.find(
          (item: any) => item.x === date
        );
        const [year, month, day] = date.split("-"); // ** Date conversion logic (yyyy-mm-dd -> dd-mm-yyyy) **
        const formattedDate =
          day && month && year ? `${day}-${month}-${year}` : date; // If date is valid, convert it
        return {
          x: formattedDate,
          y: data?.y || 0,
          z: target?.z || 0,
          s: achievement?.s || 0,
        };
      });

  return (
    <>
      <Grid container spacing={3} mb={2} className="ratedTableWrap">
        <Grid item xs={6}>
          <Typography sx={{ fontSize: "18px", color: "#000000" }}>
            LCP (Largest Contentful Paint)
          </Typography>
          <Typography sx={{ fontSize: "14px", color: "#00000099" }}>
            in Seconds
          </Typography>
          <LineChartPro
            dataset={combinedDatasetLCP}
            series={[
              {
                type: "line", // Specify the type of the series
                dataKey: "y",
                color: "#6200EE", // Color for LCP
                curve: "linear",
              },
              {
                type: "line", // Specify the type of the series
                dataKey: "z",
                color: "#FF9800", // Color for FCP Target
                curve: "linear",
              },
              // {
              //   type: "line", // Specify the type of the series
              //   dataKey: "s",
              //   color: "#5de2e2", // Color for LCP Achievement
              //   curve: "linear",
              // },
            ]}
            xAxis={[{ scaleType: "point", dataKey: "x", disableLine: true }]}
            yAxis={[
              {
                disableLine: true,
                disableTicks: true,
                min: 0,
                position: "right",
              },
            ]}
            height={350}
            margin={{ left: 32, right: 42, top: 25, bottom: 30 }}
            sx={{
              [`& .${lineElementClasses.root}`]: {
                strokeWidth: 2,
              },
              [`& .${markElementClasses.root}`]: {
                scale: "0.6",
                strokeWidth: 2,
                "&:hover": {
                  scale: "1.2",
                  strokeWidth: 5,
                  opacity: "0.6",
                },
              },
            }}
            leftAxis={null}
            rightAxis={{}}
          >
            <ChartsGrid horizontal />
            <CustomItemTooltip />
            <ChartsXAxis />
            <ChartsYAxis />
            <LinePlot />
            <MarkPlot />
            <ChartsLegend direction="row" />
          </LineChartPro>
          <Grid
            container
            spacing={0}
            direction="row"
            mb={2}
            pt={2}
            className="ratedTableWrap"
            sx={{ pl: 6 }}
          >
            <Grid item xs={4}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  sx={{
                    width: 12,
                    height: 3,
                    backgroundColor: "#6200EE",
                    mr: 1,
                  }}
                ></Box>
                <Typography sx={{ color: "#00000099", fontSize: "12px" }}>
                  Real Users Experience (LCP)
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  sx={{
                    width: 12,
                    height: 3,
                    backgroundColor: "#FF9800",
                    mr: 1,
                  }}
                ></Box>
                <Typography sx={{ color: "#00000099", fontSize: "12px" }}>
                  LCP Target
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              {/* <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  sx={{
                    width: 12,
                    height: 3,
                    backgroundColor: "#5de2e2",
                    mr: 1,
                  }}
                ></Box>
                <Typography sx={{ color: "#00000099", fontSize: "12px" }}>
                  LCP Achievement
                </Typography>
              </Box> */}
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={6}>
          <Typography sx={{ fontSize: "18px", color: "#000000" }}>
            INP (Interaction to Next Paint)
          </Typography>
          <Typography sx={{ fontSize: "14px", color: "#00000099" }}>
            in Milliseconds
          </Typography>
          <LineChartPro
            dataset={combinedDatasetINP}
            series={[
              {
                type: "line", // Specify the type of the series
                dataKey: "y",
                color: "#6200EE", // Color for LCP
                curve: "linear",
              },
              {
                type: "line", // Specify the type of the series
                dataKey: "z",
                color: "#FF9800", // Color for FCP Target
                curve: "linear",
              },
              // {
              //   type: "line", // Specify the type of the series
              //   dataKey: "s",
              //   color: "#5de2e2", // Color for LCP Achievement
              //   curve: "linear",
              // },
            ]}
            xAxis={[{ scaleType: "point", dataKey: "x", disableLine: true }]}
            yAxis={[
              {
                disableLine: true,
                disableTicks: true,
                position: "right",
              },
            ]}
            height={350}
            margin={{ left: 32, right: 42, top: 25, bottom: 30 }}
            sx={{
              [`& .${lineElementClasses.root}`]: {
                strokeWidth: 2,
              },
              [`& .${markElementClasses.root}`]: {
                scale: "0.6",
                strokeWidth: 2,
                "&:hover": {
                  scale: "1.2",
                  strokeWidth: 5,
                  opacity: "0.6",
                },
              },
            }}
            leftAxis={null}
            rightAxis={{}}
          >
            <ChartsGrid horizontal />
            <CustomItemTooltip />
            <ChartsXAxis />
            <ChartsYAxis />
            <LinePlot />
            <MarkPlot />
            <ChartsLegend direction="row" />
          </LineChartPro>
          <Grid
            container
            spacing={0}
            direction="row"
            mb={2}
            pt={2}
            className="ratedTableWrap"
            sx={{ pl: 6 }}
          >
            <Grid item xs={4}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  sx={{
                    width: 12,
                    height: 3,
                    backgroundColor: "#6200EE",
                    mr: 1,
                  }}
                ></Box>
                <Typography sx={{ color: "#00000099", fontSize: "12px" }}>
                  Real Users Experience (INP)
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  sx={{
                    width: 12,
                    height: 3,
                    backgroundColor: "#FF9800",
                    mr: 1,
                  }}
                ></Box>
                <Typography sx={{ color: "#00000099", fontSize: "12px" }}>
                  INP Target
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {/* <Box
                  sx={{
                    width: 12,
                    height: 3,
                    backgroundColor: "#5de2e2",
                    mr: 1,
                  }}
                ></Box>
                <Typography sx={{ color: "#00000099", fontSize: "12px" }}>
                  INP Achievement
                </Typography> */}
              </Box>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={6}>
          <Typography sx={{ fontSize: "18px", color: "#000000" }}>
            CLS (Cumulative Layout Shift)
          </Typography>
          <Typography sx={{ fontSize: "14px", color: "#00000099" }}>
            in Milliseconds
          </Typography>
          <LineChartPro
            dataset={combinedDatasetCLS}
            series={[
              {
                type: "line", // Specify the type of the series
                dataKey: "y",
                color: "#6200EE", // Color for LCP
                curve: "linear",
              },
              {
                type: "line", // Specify the type of the series
                dataKey: "z",
                color: "#FF9800", // Color for FCP Target
                curve: "linear",
              },
              // {
              //   type: "line", // Specify the type of the series
              //   dataKey: "s",
              //   color: "#5de2e2", // Color for LCP Achievement
              //   curve: "linear",
              // },
            ]}
            xAxis={[{ scaleType: "point", dataKey: "x", disableLine: true }]}
            yAxis={[
              {
                disableLine: true,
                disableTicks: true,
                min: 0,
                max: 2,
                position: "right",
              },
            ]}
            height={350}
            margin={{ left: 32, right: 42, top: 25, bottom: 30 }}
            sx={{
              [`& .${lineElementClasses.root}`]: {
                strokeWidth: 2,
              },
              [`& .${markElementClasses.root}`]: {
                scale: "0.6",
                strokeWidth: 2,
                "&:hover": {
                  scale: "1.2",
                  strokeWidth: 5,
                  opacity: "0.6",
                },
              },
            }}
            leftAxis={null}
            rightAxis={{}}
          >
            <ChartsGrid horizontal />
            <CustomItemTooltip />
            <ChartsXAxis />
            <ChartsYAxis />
            <LinePlot />
            <MarkPlot />
            <ChartsLegend direction="row" />
          </LineChartPro>
          <Grid
            container
            spacing={0}
            direction="row"
            mb={2}
            pt={2}
            className="ratedTableWrap"
            sx={{ pl: 6 }}
          >
            <Grid item xs={4}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  sx={{
                    width: 12,
                    height: 3,
                    backgroundColor: "#6200EE",
                    mr: 1,
                  }}
                ></Box>
                <Typography sx={{ color: "#00000099", fontSize: "12px" }}>
                  Real Users Experience (CLS)
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  sx={{
                    width: 12,
                    height: 3,
                    backgroundColor: "#FF9800",
                    mr: 1,
                  }}
                ></Box>
                <Typography sx={{ color: "#00000099", fontSize: "12px" }}>
                  CLS Target
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              {/* <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  sx={{
                    width: 12,
                    height: 3,
                    backgroundColor: "#5de2e2",
                    mr: 1,
                  }}
                ></Box>
                <Typography sx={{ color: "#00000099", fontSize: "12px" }}>
                  CLS Achievement
                </Typography>
              </Box> */}
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={6}>
          <Typography sx={{ fontSize: "18px", color: "#000000" }}>
            FCP (First Contentful Paint)
          </Typography>
          <Typography sx={{ fontSize: "14px", color: "#00000099" }}>
            in Seconds
          </Typography>
          <LineChartPro
            dataset={combinedDatasetFCP}
            series={[
              {
                type: "line", // Specify the type of the series
                dataKey: "y",
                color: "#6200EE", // Color for LCP
                curve: "linear",
              },
              {
                type: "line", // Specify the type of the series
                dataKey: "z",
                color: "#FF9800", // Color for FCP Target
                curve: "linear",
              },
              // {
              //   type: "line", // Specify the type of the series
              //   dataKey: "s",
              //   color: "#5de2e2", // Color for LCP Achievement
              //   curve: "linear",
              // },
            ]}
            xAxis={[{ scaleType: "point", dataKey: "x", disableLine: true }]}
            yAxis={[
              {
                disableLine: true,
                disableTicks: true,
                min: 0, // Set the minimum value for the y-axis
                max: 4, // Set the maximum value for the y-axis
                position: "right",
              },
            ]}
            height={350}
            margin={{ left: 32, right: 42, top: 25, bottom: 30 }}
            sx={{
              [`& .${lineElementClasses.root}`]: {
                strokeWidth: 2,
              },
              [`& .${markElementClasses.root}`]: {
                scale: "0.6",
                strokeWidth: 2,
                "&:hover": {
                  scale: "1.2",
                  strokeWidth: 5,
                  opacity: "0.6",
                },
              },
            }}
            leftAxis={null}
            rightAxis={{}}
          >
            <ChartsGrid horizontal />
            <CustomItemTooltip />
            <ChartsXAxis />
            <ChartsYAxis />
            <LinePlot />
            <MarkPlot />
            <ChartsLegend direction="row" />
          </LineChartPro>
          <Grid
            container
            spacing={0}
            direction="row"
            mb={2}
            pt={2}
            className="ratedTableWrap"
            sx={{ pl: 6 }}
          >
            <Grid item xs={4}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  sx={{
                    width: 12,
                    height: 3,
                    backgroundColor: "#6200EE",
                    mr: 1,
                  }}
                ></Box>
                <Typography sx={{ color: "#00000099", fontSize: "12px" }}>
                  Real Users Experience (FCP)
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  sx={{
                    width: 12,
                    height: 3,
                    backgroundColor: "#FF9800",
                    mr: 1,
                  }}
                ></Box>
                <Typography sx={{ color: "#00000099", fontSize: "12px" }}>
                  FCP Target
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              {/* <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  sx={{
                    width: 12,
                    height: 3,
                    backgroundColor: "#5de2e2",
                    mr: 1,
                  }}
                ></Box>
                <Typography sx={{ color: "#00000099", fontSize: "12px" }}>
                  FCP Achievement
                </Typography>
              </Box> */}
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={6}>
          <Typography sx={{ fontSize: "18px", color: "#000000" }}>
            TTFB (Time to First Byte)
          </Typography>
          <Typography sx={{ fontSize: "14px", color: "#00000099" }}>
            in Seconds
          </Typography>
          <LineChartPro
            dataset={combinedDatasetTTFB}
            series={[
              {
                type: "line", // Specify the type of the series
                dataKey: "y",
                color: "#6200EE", // Color for LCP
                curve: "linear",
              },
              {
                type: "line", // Specify the type of the series
                dataKey: "z",
                color: "#FF9800", // Color for FCP Target
                curve: "linear",
              },
              // {
              //   type: "line", // Specify the type of the series
              //   dataKey: "s",
              //   color: "#5de2e2", // Color for LCP Achievement
              //   curve: "linear",
              // },
            ]}
            xAxis={[{ scaleType: "point", dataKey: "x", disableLine: true }]}
            height={350}
            yAxis={[
              {
                disableLine: true,
                disableTicks: true,
                min: 0, // Set the minimum value for the y-axis
                max: 2, // Set the maximum value for the y-axis
                position: "right",
              },
            ]}
            margin={{ left: 32, right: 32, top: 25, bottom: 30 }}
            sx={{
              [`& .${lineElementClasses.root}`]: {
                strokeWidth: 2,
              },
              [`& .${markElementClasses.root}`]: {
                scale: "0.6",
                strokeWidth: 2,
                "&:hover": {
                  scale: "1.2",
                  strokeWidth: 5,
                  opacity: "0.6",
                },
              },
            }}
            leftAxis={null}
            rightAxis={{}}
          >
            <ChartsGrid horizontal />
            <CustomItemTooltip />
            <ChartsXAxis />
            <ChartsYAxis />
            <LinePlot />
            <MarkPlot />
            <ChartsLegend direction="row" />
          </LineChartPro>
          <Grid
            container
            spacing={0}
            direction="row"
            mb={2}
            pt={2}
            className="ratedTableWrap"
            sx={{ pl: 6 }}
          >
            <Grid item xs={4}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  sx={{
                    width: 12,
                    height: 3,
                    backgroundColor: "#6200EE",
                    mr: 1,
                  }}
                ></Box>
                <Typography sx={{ color: "#00000099", fontSize: "12px" }}>
                  Real Users Experience (TTFB)
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  sx={{
                    width: 12,
                    height: 3,
                    backgroundColor: "#FF9800",
                    mr: 1,
                  }}
                ></Box>
                <Typography sx={{ color: "#00000099", fontSize: "12px" }}>
                  TTFB Target
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              {/* <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  sx={{
                    width: 12,
                    height: 3,
                    backgroundColor: "#5de2e2",
                    mr: 1,
                  }}
                ></Box>
                <Typography sx={{ color: "#00000099", fontSize: "12px" }}>
                  TTFB Achievement
                </Typography>
              </Box> */}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default PerformanceDashboardRealUsersExperience;
